import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import {LoadingButton} from '@mui/lab';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
} from '@mui/material';
import {useFormik} from 'formik';
import {useSnackbar} from 'notistack';
import {useEffect, useState} from 'react';
import * as yup from 'yup';

import API, {getMessagesFromApiError} from '../../api/axios';
import {apiBaseUrl} from '../../api/urls';
import {
  UserGroup,
  UserGroupInputBody,
  UserGroupItemResponse,
} from '../../interfaces/UserGroup';
import {CloseSnackbarAction} from '../common/CloseSnackbarButton';
import SnackbarMessages from '../common/SnackbarMessages';

interface Props {
  pk?: number;
  item?: UserGroup;
  prefetch?: boolean;
  onClose?: () => void;
  onSubmitted?: (item: UserGroup) => void;
}

const inputValidationSchema = yup.object().shape({
  name: yup.string().nullable().required('Field is required').max(50),
  priority: yup.number().nullable().integer('Must be a whole number'),
  description: yup.string().nullable(),
  status: yup.string().nullable().required('Field is required'),
});

const getFormikValues = (item?: UserGroup): UserGroupInputBody => ({
  name: item?.name ?? null,
  priority: item?.priority ?? null,
  description: item?.description ?? null,
  status: item?.status ?? 'active',
});

const UserGroupItemUpsert = ({
  pk,
  item,
  prefetch,
  onClose,
  onSubmitted,
}: Props) => {
  /*********/
  /* fetch */
  /*********/
  const [fetchedData, setFetchedData] = useState<UserGroupItemResponse | undefined>(item);
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const fetchData = async () => {
    setFetchedInProgress(true);
    try {
      const resp = await API.get<UserGroupItemResponse>(
        `${apiBaseUrl}/user-group/${pk}`
      );
      setFetchedData(resp.data);
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_ug010001');
      setFetchedErrors(messages);
    }
    setFetchedInProgress(false);
  };

  useEffect(() => {
    if (prefetch && pk) {
      fetchData();
    }
  }, [pk, prefetch]);

  /**********/
  /* submit */
  /**********/
  const {enqueueSnackbar} = useSnackbar();
  const [submittedInProgress, setSubmittedInProgress] = useState(false);

  const submitData = async (data: UserGroupInputBody) => {
    setSubmittedInProgress(true);
    try {
      const endpoint = pk
        ? `${apiBaseUrl}/user-group/${pk}`
        : `${apiBaseUrl}/user-group`;
      const resp = pk
        ? await API.patch<UserGroup>(endpoint, data)
        : await API.post<UserGroup>(endpoint, data);
      const message = `User Group has been ${pk ? 'updated' : 'created'}`;
      enqueueSnackbar(message, {
        variant: 'success',
        action: CloseSnackbarAction,
      });
      onSubmitted?.(resp.data);
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_ug010002');
      enqueueSnackbar(<SnackbarMessages messages={messages} />, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    }
    setSubmittedInProgress(false);
  };

  /*********/
  /* input */
  /*********/
  const formik = useFormik<UserGroupInputBody>({
    initialValues: getFormikValues(fetchedData),
    validationSchema: inputValidationSchema,
    onSubmit: async (values) => {
      await submitData(values);
    },
  });

  useEffect(() => {
    formik.setValues(getFormikValues(fetchedData));
  }, [fetchedData]);

  const ActionIcon = pk ? EditIcon : AddIcon;
  const actionName = pk ? 'Update' : 'Create';

  return (
    <Box position="relative" gap={3} p={3}>
      <Box display="flex" justifyContent="space-between">
        <Box display="flex" alignItems="center" width="100%" gap={1.5}>
          <ActionIcon sx={{color: 'primary.main'}} />
          <Box fontSize={24}>{actionName} User Group</Box>
        </Box>
        {onClose ? (
          <IconButton onClick={() => onClose()}>
            <CloseIcon />
          </IconButton>
        ) : null}
      </Box>

      <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {fetchedErrors.map((error, index) => (
        <Alert key={index} severity="error">
          {error}
        </Alert>
      ))}

      <Box component="form" position="relative" onSubmit={formik.handleSubmit}>
        <Box my={4}>
          <TextField
            value={formik.values.name ?? ''}
            fullWidth
            name="name"
            label="Name"
            size="small"
            error={!!formik.touched.name && !!formik.errors.name}
            helperText={formik.touched.name && formik.errors.name}
            onChange={(event) => {
              formik.setFieldValue('name', event.target.value || null);
            }}
          />
        </Box>

        <Box my={4}>
          <TextField
            value={formik.values.priority ?? ''}
            fullWidth
            name="priority"
            label="Priority"
            size="small"
            type="number"
            error={!!formik.touched.priority && !!formik.errors.priority}
            helperText={formik.touched.priority && formik.errors.priority}
            onChange={(event) => {
              formik.setFieldValue(
                'priority',
                event.target.value !== '' ? Number(event.target.value) : null
              );
            }}
          />
        </Box>

        <Box my={4}>
          <TextField
            value={formik.values.description ?? ''}
            fullWidth
            name="description"
            label="Description"
            size="small"
            multiline
            rows={3}
            error={!!formik.touched.description && !!formik.errors.description}
            helperText={formik.touched.description && formik.errors.description}
            onChange={(event) => {
              formik.setFieldValue(
                'description',
                event.target.value || null
              );
            }}
          />
        </Box>

        <Box my={4}>
          <TextField
            value={formik.values.status ?? ''}
            fullWidth
            name="status"
            label="Status"
            select
            size="small"
            error={!!formik.touched.status && !!formik.errors.status}
            helperText={formik.touched.status && formik.errors.status}
            onChange={formik.handleChange}
          >
            {[
              {value: 'active', name: 'Active'},
              {value: 'inactive', name: 'Inactive'},
            ].map((i) => (
              <MenuItem key={i.value} value={i.value}>
                {i.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {fetchedErrors.map((error, index) => (
          <Alert key={index} severity="error" sx={{my: 2}}>
            {error}
          </Alert>
        ))}

        <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
          <Button onClick={() => onClose?.()}>Cancel</Button>
          <LoadingButton
            sx={{ml: 1}}
            variant="contained"
            loading={submittedInProgress}
            type="submit"
          >
            Submit
          </LoadingButton>
        </Box>
      </Box>
    </Box>
  );
};

export default UserGroupItemUpsert;
